// src/modules/app-views/invoices/components/CardHeaderInvoiceOrderDateSeller.jsx
// *******************************************************************************
// CARD HEADER: Order Number, Nature of Operation, Seller Name
// *******************************************************************************
// Column 2 of the invoice document card header.
// Shows: Order Number (with badge for multiple orders), Nature of Operation, Seller Name.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tooltip, Form } from 'antd';
import SimpleColumn from 'components/SimpleComponents/SimpleColumn';
import { ShoppingOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
export const CardHeaderInvoiceOperationOrderSeller = ({
  form,
  showItem1 = true,
  showItem2 = true,
  showItem3 = true,
  forceShow = false,
  ...record
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch form value reactively for new documents
  const formNatureOfOperation = Form.useWatch('natureOfOperation', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isNew = !record?._id || record?._id === 'add';

  // Order number (can be array with multiple orders)
  const rawOrder = record?.orderNumber;
  const rawLegacy = record?.orderLegacyCode;

  // Handle orderNumber as array or single value
  const ordersArray = Array.isArray(rawOrder)
    ? rawOrder
    : rawOrder
      ? [rawOrder]
      : Array.isArray(rawLegacy)
        ? rawLegacy
        : [];

  // Build order number label with badge for multiple orders
  const orderNumberLabel = buildOrderNumberLabel(ordersArray);

  // Nature of operation (Natureza da Operação)
  // For new documents, use form value; for existing, use record value
  const rawNatureOfOperation = isNew ? formNatureOfOperation : record?.natureOfOperation;
  // Map value to translation key (e.g., 'sale' -> 'nature_sale')
  const operationNature = rawNatureOfOperation
    ? translateX(getNatureOfOperationLabelKey(rawNatureOfOperation))
    : null;

  // Seller name (N/D for now - field may not exist yet)
  const sellerName = record?.sellerName || record?.seller?.name || null;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const items = [
    {
      key: 'natureOfOperation',
      icon: <SwapOutlined />,
      label: operationNature || <span className="text-muted opacity-0-6">N/D</span>,
      tooltipText: translateX('nature_of_operation'),
      tooltipPlacement: 'right',
      onClick: null,
      hidden: !showItem2,
    },
    {
      key: 'orderNumber',
      icon: <ShoppingOutlined />,
      label: orderNumberLabel || <span className="text-muted opacity-0-6">N/D</span>,
      tooltipText: translateX('order_number'),
      tooltipPlacement: 'right',
      onClick: null,
      hidden: !showItem1,
    },
    {
      key: 'sellerName',
      icon: <UserOutlined />,
      label: sellerName || <span className="text-muted opacity-0-6">N/D</span>,
      tooltipText: translateX('seller'),
      tooltipPlacement: 'right',
      onClick: null,
      hidden: !showItem3,
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return <SimpleColumn items={items?.filter(i => (i?.label || forceShow) && !i.hidden)} />;
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CardHeaderInvoiceOperationOrderSeller;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build order number label with badge for multiple orders
 * @param {Array} ordersArray - Array of order numbers
 * @returns {React.ReactNode|null} Formatted label with badge or null
 */
function buildOrderNumberLabel(ordersArray) {
  if (ordersArray.length === 0) return null;

  const firstOrder = ordersArray[0];
  const extraCount = ordersArray.length - 1;

  // Build tooltip with all orders if there are multiple
  const tooltipContent = ordersArray.length > 1 ? ordersArray.join(', ') : null;

  const content = (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span>{firstOrder}</span>
      {extraCount > 0 && (
        <span
          style={{
            backgroundColor: '#1890ff',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            padding: '0 6px',
            borderRadius: 10,
            lineHeight: '18px',
            whiteSpace: 'nowrap',
          }}
        >
          +{extraCount}
        </span>
      )}
    </span>
  );

  // Wrap with tooltip if there are multiple orders
  if (tooltipContent) {
    return <Tooltip title={tooltipContent}>{content}</Tooltip>;
  }

  return content;
}

/**
 * Get translation key for nature of operation value
 * Maps values like 'sale' to 'nature_sale' to match NATURE_OF_OPERATION_OPTIONS
 * @param {string} value - Nature of operation value (e.g., 'sale', 'return', 'transfer')
 * @returns {string} Translation key (e.g., 'nature_sale', 'nature_return', 'nature_transfer')
 */
function getNatureOfOperationLabelKey(value) {
  if (!value) return null;

  // Map of value to translation key (matches NATURE_OF_OPERATION_OPTIONS in ConfigCard.jsx)
  const labelKeyMap = {
    sale: 'nature_sale',
    return: 'nature_return',
    transfer: 'nature_transfer',
    bonus: 'nature_bonus',
    demo: 'nature_demo',
    sample: 'nature_sample',
    consignment: 'nature_consignment',
    other: 'nature_other',
  };

  return labelKeyMap[value] || value;
}
